import { EXPANDABLE_COL } from '@/global/components/DataGrid/components/ExpandCell'
import ColumnEnum from '@/global/components/DataGrid/utils/columEnum'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import { cpfMask, cpfValido, parserRegisterOrigin } from '@acme/common'
import { StatusAprovacao } from '@acme/core'
import { Row } from '@tanstack/react-table'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { match } from 'ts-pattern'
import { Status } from 'templates/ManutencaoDoPonto/Ajuste/atoms/StatusChip'
import VisualizarAnexoModal from 'templates/ManutencaoDoPonto/Ajuste/atoms/VisualizarAnexo'
import { linhasFolhaDoGrupo } from '../PontoDiario/contrato-agrupamento'
import { LinhaExplodidaAjuste } from './explodir-linhas'
import {
    CAMPO_POR_NIVEL,
    LABEL_NIVEL,
    NivelAjuste,
    ORDEM_NIVEIS,
} from './niveis-agrupamento'

dayjs.extend(utc)

export { CAMPO_POR_NIVEL }

const NIVEL_POR_CAMPO = Object.fromEntries(
    Object.entries(CAMPO_POR_NIVEL).map(([nivel, campo]) => [campo, nivel]),
) as Record<string, NivelAjuste>

const CHIP_POR_PROFUNDIDADE: Record<number, string> = {
    0: 'bg-primary-800',
    1: 'bg-primary-700',
    2: 'bg-primary-600',
    3: 'bg-primary-500',
}

function ColunaFuncionario({
    tableRow,
    value,
}: {
    tableRow?: Row<LinhaExplodidaAjuste>
    value?: string
}) {
    if (!tableRow) return null

    if (tableRow.getIsGrouped()) {
        const campoGrupo = tableRow.groupingColumnId as string
        const nivel = NIVEL_POR_CAMPO[campoGrupo]

        return (
            <span
                className="flex items-center gap-2"
                style={{ paddingLeft: tableRow.depth * 24 }}
            >
                <span
                    className={cn(
                        CHIP_POR_PROFUNDIDADE[tableRow.depth] ??
                            'bg-primary-500',
                        'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white',
                    )}
                >
                    {LABEL_NIVEL[nivel] ?? campoGrupo}
                </span>
                <span className="font-bold">
                    {tableRow.getValue<string>(campoGrupo)}
                </span>
                <span className="text-primary-600 text-xs font-normal">
                    ({linhasFolhaDoGrupo(tableRow).length})
                </span>
            </span>
        )
    }

    return (
        <span>
            {value ??
                tableRow.original._funcionarioNome ??
                tableRow.original.Funcionario?.nome ??
                ''}
        </span>
    )
}

export function colunasParaNiveis(
    niveisSelecionados: Set<NivelAjuste>,
): GridColDefPro<LinhaExplodidaAjuste>[] {
    const colunasOcultas: GridColDefPro<LinhaExplodidaAjuste>[] =
        ORDEM_NIVEIS.filter(nivel => niveisSelecionados.has(nivel)).map(
            nivel => ({
                field: CAMPO_POR_NIVEL[nivel] as any,
                headerName: LABEL_NIVEL[nivel],
                hide: true,
            }),
        )

    const colunasVisiveis: GridColDefPro<LinhaExplodidaAjuste>[] = [
        EXPANDABLE_COL,
        {
            field: 'Criador' as any,
            headerName: 'Incluído por',
            type: new ColumnEnum([
                { value: 'Funcionário', label: 'Funcionário' },
                { value: 'Supervisor', label: 'Supervisor' },
                { value: 'Administrador', label: 'Administrador' },
                { value: 'Desconhecido', label: 'Desconhecido' },
            ]),
            renderCell: ({ value }) => {
                if (!value) return 'Desconhecido'

                const title = cpfValido(value.email)
                    ? cpfMask(value.email)
                    : value.email

                return (
                    <Tooltip title={title}>
                        <span>
                            {match(value.categoriaId)
                                .with(
                                    CategoriaEnum.Funcionario,
                                    () => 'Funcionário',
                                )
                                .with(
                                    CategoriaEnum.Supervisor,
                                    () => 'Supervisor',
                                )
                                .with(
                                    CategoriaEnum.Cliente,
                                    CategoriaEnum.Avaliacao,
                                    () => 'Administrador',
                                )
                                .otherwise(() => 'Desconhecido')}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'tipoAjuste',
            headerName: 'Tipo',
            width: 120,
            type: new ColumnEnum([
                { value: 'Abono', label: 'Abono' },
                { value: 'Afastamento', label: 'Afastamento' },
                { value: 'Registro', label: 'Registro' },
                { value: 'ABONO', label: 'Abono' },
                { value: 'AFASTAMENTO', label: 'Afastamento' },
                { value: 'REGISTRO', label: 'Registro' },
            ]),
            valueFormatter: ({ value }) => {
                if (!value) return ''
                const upper = String(value).toUpperCase()
                if (upper === 'ABONO') return 'Abono'
                if (upper === 'AFASTAMENTO') return 'Afastamento'
                if (upper === 'REGISTRO') return 'Registro'
                return String(value)
            },
        },
        {
            field: 'data',
            headerName: 'Data',
            width: 180,
            valueGetter: ({ row }) => row?._dataLabel || row?.data || '',
        },
        {
            field: 'Funcionario' as any,
            headerName: 'Funcionário',
            minWidth: 160,
            flex: 1,
            valueGetter: ({ row }) =>
                row?._funcionarioNome || row?.Funcionario?.nome || '',
            renderCell: ({ tableRow, value }) => (
                <ColunaFuncionario
                    tableRow={tableRow}
                    value={value as string}
                />
            ),
        },
        {
            field: 'hora',
            headerName: 'Hora',
            width: 70,
            type: 'time',
            valueFormatter: params => {
                if (!params.value) return ''
                const [hora, minuto] = String(params.value).split(':')
                return minuto !== undefined ? `${hora}:${minuto}` : hora
            },
        },
        {
            field: 'origem',
            headerName: 'Origem',
            width: 150,
            valueGetter: ({ value }) =>
                value ? parserRegisterOrigin(value).title : '',
        },
        {
            field: 'justificativa',
            headerName: 'Justificativa',
            minWidth: 100,
            maxWidth: 200,
            valueGetter: ({ row }) =>
                row?.justificativa || (row as any)?.descricao || '',
        },
        {
            field: 'fileUrl',
            headerName: 'Anexo',
            width: 100,
            renderCell: VisualizarAnexoModal as any,
        },
        {
            field: 'statusAprovacao',
            headerName: 'Status',
            align: 'center',
            headerAlign: 'center',
            width: 160,
            type: new ColumnEnum([
                { value: StatusAprovacao.APROVADO, label: 'Aprovado' },
                { value: StatusAprovacao.REPROVADO, label: 'Reprovado' },
            ]),
            renderCell: Status as any,
        },
        {
            field: 'updatedAt' as any,
            headerName: 'Data de aprovação',
            width: 160,
            type: 'date',
            valueFormatter: params => {
                if (!params.value) return ''
                return dayjs(params.value).format('DD/MM/YYYY HH:mm')
            },
        },
    ]

    return [...colunasOcultas, ...colunasVisiveis]
}
